import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  GetDeliveredEngagementSurveyQuery,
  GroupedAnswersNode,
} from "types/graphql-schema";

import AssessmentLayout from "@apps/assessments/components/layouts/assessment-layout";
import { initEngagementSurveyReportViewOptions } from "@apps/reporting/components/engagement-survey-report/helpers";
import { EngagementSurveyReportViewOptions } from "@apps/reporting/components/engagement-survey-report/types";
import useSurveyReportFilters from "@apps/reporting/components/engagement-survey-report/use-survey-report-filters";
import useLabel from "@apps/use-label/use-label";
import { isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";

import EngagementSurveyDeliveryAllQuestions from "./engagement-survey-delivery-all-questions";
import EngagementSurveyDeliveryFilterSidebar from "./engagement-survey-delivery-filter-sidebar";
import EngagementSurveyDeliveryHeader from "./engagement-survey-delivery-header";
import useSurveyDeliveryData from "./use-survey-delivery-data";

const EngagementSurveyDelivery = ({
  assessmentDeliveryId,
  onUpdateDeliveryData,
}: {
  assessmentDeliveryId: number;
  onUpdateDeliveryData?: Dispatch<
    SetStateAction<GetDeliveredEngagementSurveyQuery["assessmentDelivery"]>
  >;
}) => {
  const label = useLabel();
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [viewOptions, setViewOptions] =
    useState<EngagementSurveyReportViewOptions>(
      initEngagementSurveyReportViewOptions,
    );

  const isAdmin = isAdminVar();

  const handleUpdateViewOptions = useCallback(
    (opts: Partial<EngagementSurveyReportViewOptions>) => {
      setViewOptions((prev) => ({
        ...prev,
        ...opts,
      }));
    },
    [],
  );

  const graphqlFilters = useSurveyReportFilters({ viewOptions });
  const {
    assessmentDeliveryData,
    previousAssessmentDeliveryData,
    isLoadingAssessmentDelivery,
  } = useSurveyDeliveryData({
    assessmentDeliveryId,
    viewOptions,
    filters: graphqlFilters,
    onCompleted: (response: GetDeliveredEngagementSurveyQuery) => {
      if (onUpdateDeliveryData && response.assessmentDelivery) {
        onUpdateDeliveryData(response.assessmentDelivery);
      }
    },
  });

  const assessmentDelivery = useMemo(
    () =>
      assessmentDeliveryData
        ? assessmentDeliveryData.assessmentDelivery
        : previousAssessmentDeliveryData
          ? previousAssessmentDeliveryData.assessmentDelivery
          : null,
    [assessmentDeliveryData, previousAssessmentDeliveryData],
  );

  const groupedAnswers = useMemo(() => {
    return assessmentDelivery?.groupedAnswers
      ? assessmentDelivery.groupedAnswers.filter(
          (answer): answer is GroupedAnswersNode => answer !== null,
        )
      : [];
  }, [assessmentDelivery?.groupedAnswers]);

  return (
    <>
      {showFilterSidebar && (
        <EngagementSurveyDeliveryFilterSidebar
          viewOptions={viewOptions}
          onUpdateViewOptions={handleUpdateViewOptions}
          groupedAnswers={groupedAnswers}
          onClose={() => setShowFilterSidebar(false)}
          isLoading={isLoadingAssessmentDelivery}
        />
      )}
      <Layout.Container loading={isLoadingAssessmentDelivery}>
        <Layout.Main width="default">
          <div className="mt-8" aria-label="Surveys > delivery">
            <div className="flex flex-col">
              <EngagementSurveyDeliveryHeader
                assessmentDeliveryData={assessmentDeliveryData}
              />

              <div className="mb-12 flex items-center justify-between">
                <AssessmentLayout.AssessmentTypeHeader>
                  All Questions
                </AssessmentLayout.AssessmentTypeHeader>

                <div className="flex items-center gap-3">
                  {!isAdmin &&
                    assessmentDelivery &&
                    !assessmentDelivery.complianceProgram
                      ?.hasAccessToAppliesToUsers && (
                      <Tooltip
                        text={`You do not have the access privileges to view all the responses within the ${label(
                          "organization",
                        )}.`}
                      >
                        <div className="text-sm bg-amber-100 py-2 px-3 rounded-md flex items-center gap-2">
                          <TopicflowIcon
                            type="warning"
                            className="text-amber-700 h-5 w-5"
                          />
                          <div>Not all responses are shown</div>
                        </div>
                      </Tooltip>
                    )}
                  <Button
                    to={`/programs/${assessmentDelivery?.complianceProgram?.id}/reporting/engagement`}
                    text="View Reporting"
                  />
                  <Button
                    className="flex items-center"
                    theme={buttonTheme.iconGray}
                    onClick={() => setShowFilterSidebar(true)}
                    aria-label="Filters"
                    icon={TopicflowIconMap.adjustments}
                  />
                </div>
              </div>

              <EngagementSurveyDeliveryAllQuestions
                assessmentDeliveryData={assessmentDeliveryData}
                viewOptions={viewOptions}
              />
            </div>
          </div>
        </Layout.Main>
      </Layout.Container>
    </>
  );
};

export default EngagementSurveyDelivery;
